import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import companyServices from "app/services/company-services";
import { useSnackbar } from "notistack";

const CONFIRM_WORD = "ELIMINAR";

// Borrado definitivo, el paso que sigue a dar de baja cuando la compañía no tiene
// que existir más. Se lleva la operación y no vuelve, así que el modal muestra
// primero el tamaño de lo que va a borrar y recién después pide la palabra: una
// cuenta de 300 pedidos frena a tiempo a quien se equivocó de tarjeta.
const CompanyDeleteModal = ({
    company = null,
    open = false,
    onClose = () => {},
    onDeleted = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [plan, setPlan] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);

    const loadPlan = useCallback(async (companyId) => {
        try {
            setLoadingPlan(true);
            setPlan(await companyServices.getCompanyDeletionPlan(companyId));
        } catch (_error) {
            // El interceptor muestra el mensaje; sin plan no se habilita el botón.
            setPlan(null);
        } finally {
            setLoadingPlan(false);
        }
    }, []);

    useEffect(() => {
        if (!open || !company?.id) return;

        setConfirmText("");
        setPlan(null);
        loadPlan(company.id);
    }, [open, company?.id, loadPlan]);

    const handleClose = () => {
        setConfirmText("");
        setDeleting(false);
        setPlan(null);
        onClose();
    };

    const confirmed = confirmText.trim().toUpperCase() === CONFIRM_WORD;

    const remove = async () => {
        if (!confirmed || !company) return;

        try {
            setDeleting(true);
            const response = await companyServices.deleteCompany(company.id, confirmText.trim().toUpperCase());
            enqueueSnackbar(`${response.company_name || company.name} se eliminó definitivamente`, { variant: "success" });
            onDeleted();
            handleClose();
        } catch (_error) {
            // El interceptor muestra el mensaje del backend.
        } finally {
            setDeleting(false);
        }
    };

    const counts = plan?.counts || {};
    const operational = plan?.operational || {};
    const usersToDelete = plan?.users_to_delete || [];
    const usersKept = plan?.users_kept || [];
    const hasOperation = Object.keys(operational).length > 0;
    const nothingToDelete = !loadingPlan && plan && Object.keys(counts).length === 0;

    const renderCounts = (entries) => (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.25 }}>
            {entries.map(([label, count]) => (
                <React.Fragment key={label}>
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{label}</Typography>
                    <Typography variant="body2" fontWeight={700} align="right">{count}</Typography>
                </React.Fragment>
            ))}
        </Box>
    );

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
            <DialogTitle>
                <Typography variant="h2" color="error">Eliminar definitivamente</Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>{company?.name}</strong>{company?.id ? ` (ID: ${company.id})` : ""}
                </Typography>

                {loadingPlan && (
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                            Contando lo que se va a borrar…
                        </Typography>
                    </Stack>
                )}

                {!loadingPlan && plan && (
                    <>
                        {hasOperation && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Esta compañía ya operó. También se borra su historial:
                                </Typography>
                                {renderCounts(Object.entries(operational))}
                            </Alert>
                        )}

                        {nothingToDelete ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No tiene datos asociados: se borra solo la compañía.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Se va a borrar:</Typography>
                                {renderCounts(Object.entries(counts).sort((a, b) => b[1] - a[1]))}
                            </>
                        )}

                        {usersToDelete.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Estos usuarios se eliminan, porque no trabajan en ninguna otra compañía:
                                </Typography>
                                {usersToDelete.map((email) => (
                                    <Typography key={email} variant="body2" color="text.secondary">· {email}</Typography>
                                ))}
                            </>
                        )}

                        {usersKept.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Estos se conservan; solo pierden su perfil en esta compañía:
                                </Typography>
                                {usersKept.map((email) => (
                                    <Typography key={email} variant="body2" color="text.secondary">· {email}</Typography>
                                ))}
                            </>
                        )}

                        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                            Esto no se puede deshacer. Si solo quieres sacarla de circulación,
                            dar de baja conserva todo y es reversible.
                        </Alert>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Escribe <strong>{CONFIRM_WORD}</strong> para confirmar:
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={confirmText}
                            autoComplete="off"
                            onChange={(event) => setConfirmText(event.target.value)}
                            placeholder={CONFIRM_WORD}
                            error={confirmText.length > 0 && !confirmed}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <LoadingButton
                    loading={deleting}
                    variant="contained"
                    color="error"
                    size="small"
                    endIcon={<DeleteForeverOutlinedIcon />}
                    loadingPosition="end"
                    disabled={!confirmed || loadingPlan || !plan}
                    onClick={remove}
                >
                    {deleting ? "Eliminando" : "Eliminar definitivamente"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CompanyDeleteModal;
